/* jshint browser:true */

/**
 * Adds functionality to Cookie Consent Dialog component.
 */
export default class CookieConsent {
  /**
   * Constructor.
   */
  constructor() {
    this.options = {
      transitionDuration: 250,
      cookieName: 'renascareConsentCookie',
    };

    this.classNames = {
      buttonsWrapper: 'js-cookie-consent__actions',
      dialogInitialized: 'js-cookie-consent--ready',
      dialogVisible: 'js-cookie-consent--visible',
      dialogActive: 'js-cookie-consent--active',
    };
  }

  /**
   * Initializes component.
   * @param {string} selector CSS selector to find dialog.
   */
  init(selector) {
    const dialog = document.querySelector(selector);

    if (!dialog) {
      // No dialog to initialize. Fail silently.
      return;
    }

    this.setup(dialog);
  }

  /**
   * Sets up the dialog component functionality.
   * @param {HTMLDialogElement} dialog The DIALOG element.
   */
  setup(dialog) {
    // Return if cookies already accepted
    if (
      this.hasConsentCookie() &&
      typeof window.RavenPatternLibrary === 'undefined'
    ) {
      this.hideDialog(dialog);
      return;
    }

    // Add buttons
    this.disableSummary(dialog);
    this.addButtons(dialog);

    // Show dialog
    this.showDialog(dialog);

    // Done
    dialog.classList.add(this.classNames.dialogInitialized);
  }

  /**
   * Disables the default click handler on a dialog's SUMMARY element.
   * @param {HTMLDialogElement} dialog  The DIALOG element.
   */
  disableSummary(dialog) {
    const dialogSummary = dialog.querySelector('summary');

    dialogSummary.addEventListener('click', (e) => e.preventDefault());
  }

  /**
   * Adds an acceptance button and a "more details" button to a dialog.
   * @param {HTMLDialogElement} dialog The DIALOG element.
   */
  addButtons(dialog) {
    const dialogSummaryWrapper = dialog.querySelector('.summary__wrapper');
    const details = dialog.querySelector('details');
    const wrapper = document.createElement('span');
    const acceptButton = document.createElement('button');
    const moreButton = document.createElement('button');
    const acceptText = dialog.dataset.acceptText || 'I Accept';
    const moreText = dialog.dataset.moreText || 'Tell Me More';
    const lessText = dialog.dataset.lessText || 'Tell Me Less';

    // Wrapper
    wrapper.className = this.classNames.buttonsWrapper;

    // Accept button
    acceptButton.setAttribute('type', 'button');
    acceptButton.className = 'r-button';
    acceptButton.innerText = acceptText;

    acceptButton.addEventListener('click', () => {
      this.setConsentCookie();
      this.hideDialog(dialog);

      if (typeof window.RavenPatternLibrary !== 'undefined') {
        setTimeout(() => this.showDialog(dialog), 2000);
      }
    });

    wrapper.appendChild(acceptButton);

    // More button
    if ('open' in details) {
      moreButton.setAttribute('type', 'button');
      moreButton.className = 'r-button';
      moreButton.innerText = moreText;

      moreButton.addEventListener('click', () => {
        if (details.open) {
          this.collapseDetials(details, moreButton, moreText);
        } else {
          this.expandDetials(details, moreButton, lessText);
        }
      });

      wrapper.appendChild(moreButton);
    }

    // Append elements
    dialogSummaryWrapper.appendChild(wrapper);
  }

  /**
   * Shows the dialog.
   * @param {HTMLDialogElement} dialog The DIALOG element.
   */
  showDialog(dialog) {
    dialog.setAttribute('open', '');
    dialog.removeAttribute('hidden');
    document.body.classList.add(this.classNames.dialogActive);

    setTimeout(() => {
      dialog.classList.add(this.classNames.dialogVisible);
    }, this.options.transitionDuration);
  }

  /**
   * Hides the dialog from view.
   * @param {HTMLDialogElement} dialog The DIALOG element.
   */
  hideDialog(dialog) {
    dialog.classList.remove(this.classNames.dialogVisible);

    setTimeout(() => {
      dialog.removeAttribute('open');
      dialog.setAttribute('hidden', '');
      document.body.classList.remove(this.classNames.dialogActive);
    }, this.options.transitionDuration);
  }

  /**
   * Expands the details content section.
   * @param {HTMLDetailsElement} details The DETAILS element.
   * @param {HTMLButtonElement} moreButton The button that expands/collapses the
   *                                       content.
   * @param {string} buttonText The text to use for the "more button".
   */
  expandDetials(details, moreButton, buttonText) {
    details.setAttribute('open', '');
    moreButton.innerText = buttonText;
  }

  /**
   * Collapses the details content section.
   * @param {HTMLDetailsElement} details The DETAILS element.
   * @param {HTMLButtonElement} moreButton The button that expands/collapses the
   *                                       content.
   * @param {string} buttonText The text to use for the "more button".
   */
  collapseDetials(details, moreButton, buttonText) {
    details.removeAttribute('open');
    moreButton.innerText = buttonText;
  }

  /**
   * Returns whether or not a consent cookie has been set.
   *
   * @return {boolean} If the cookie is set.
   */
  hasConsentCookie() {
    return document.cookie.indexOf(`${this.options.cookieName}=true`) > -1;
  }

  /**
   * Sets a cookie indicating that the user consented.
   */
  setConsentCookie() {
    const date = new Date();
    const monthInMilliseconds = 30 * 24 * 60 * 60 * 1000;

    date.setTime(date.getTime() + monthInMilliseconds);

    const expires = date.toUTCString();

    // eslint-disable-next-line max-len
    document.cookie = `${this.options.cookieName}=true; expires=${expires}; path=/`;
  }
}
